import type { planningPreparationSchema } from 'features/libra/inspectionPlanning/planningPreparationConfig'
import {
  formatScope,
  scopeInUnit,
} from 'features/libra/inspectionPlanning/scopeMeasurement'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  ExecutionColumn,
  ExecutionRow,
  executionRowsSchema,
} from 'features/libra/sections/inspectionExecutionSection/executionTypes'
import {
  asMeasurement,
  thicknessUnit,
} from 'features/libra/sections/inspectionExecutionSection/inspectionResultsModel'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { GeckoMeasurementSystem } from 'types'
import { z } from 'zod'

import { buildWorkPackFormSections } from './buildWorkPackFormSections'
import { WorkPackSection } from './workPackTypes'

function resultValue(
  row: ExecutionRow,
  column: ExecutionColumn,
  system: GeckoMeasurementSystem
) {
  const value = row.values[column.field]
  if (value === undefined || value === '') return 'Not recorded'
  const scope = asMeasurement(value)
  if (scope && column.type === 'scope')
    return row.basis.method.scopeType
      ? formatScope(scope, row.basis.method.scopeType, system)
      : 'Scope units not configured'
  if (scope && column.type === 'thickness') {
    const unit = thicknessUnit(system === GeckoMeasurementSystem.Imperial)
    const num = scopeInUnit(scope, 'length', unit)
    return num === undefined
      ? 'Incompatible saved units'
      : `${num.toLocaleString('en-GB', { maximumFractionDigits: 4 })} ${unit.name}`
  }
  return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)
}

export function buildExecutionWorkPackSections({
  execution,
  fields,
  definitions,
  system,
  documentNames,
  performedField,
}: {
  execution: z.infer<typeof planningPreparationSchema> | undefined
  fields: FieldValues
  definitions: Record<string, OntologyField>
  system: GeckoMeasurementSystem
  documentNames: Record<string, string>
  performedField: string | undefined
}): WorkPackSection[] {
  if (!execution)
    return [
      {
        title: 'Inspection status',
        rows: [{ label: 'Findings', value: 'Not yet recorded' }],
      },
    ]
  const performed = performedField
    ? unwrapFieldValue(fields[performedField])
    : undefined
  const visible = getVisibleSectionIds(execution.form, fields)
  const sections = execution.sections.filter((section) =>
    visible.includes(section.id)
  )
  return [
    {
      title: 'Inspection status',
      rows: [
        {
          label: 'Status',
          value:
            performed === true
              ? 'Inspection recorded as performed'
              : performed === false
                ? 'Inspection not performed; findings not yet recorded'
                : 'Inspection status and findings not yet recorded',
        },
      ],
    },
    ...buildWorkPackFormSections(sections, definitions, fields),
    ...sections.flatMap((section): WorkPackSection[] => {
      if (section.type === 'document_attachments') {
        const ids = z
          .array(z.string())
          .parse(unwrapFieldValue(fields[section.field]) ?? [])
        return [
          {
            title: section.title,
            rows: ids.length
              ? ids.map((id) => ({
                  label: documentNames[id] ?? 'Document name unavailable',
                  value: `Document reference: ${id}`,
                }))
              : [{ label: 'Status', value: 'No files attached' }],
          },
        ]
      }
      if (section.type !== 'inspection_execution') return []
      const rows = executionRowsSchema.parse(
        unwrapFieldValue(fields[section.field]) ?? []
      )
      const linked = rows.map((row, index) => ({
        title: `${section.title} - ${index + 1}`,
        rows: [
          {
            label: 'Zone / inspection area',
            value: `${row.basis.zoneTitle} / ${row.basis.areaTitle}`,
          },
          { label: 'Method', value: row.basis.method.method },
          {
            label: 'Procedure / specification',
            value: row.basis.method.procedure || 'Not specified',
          },
          {
            label: 'Required coverage',
            value:
              row.basis.requiredCoveragePercent === undefined
                ? 'Not specified'
                : `${row.basis.requiredCoveragePercent}%`,
          },
          {
            label: 'Estimated scope',
            value: row.basis.method.scopeType
              ? formatScope(
                  row.basis.method.estimatedScope,
                  row.basis.method.scopeType,
                  system
                )
              : 'Not specified',
          },
          ...section.columns.map((column) => ({
            label: column.label,
            value: resultValue(row, column, system),
          })),
          {
            label: 'Record / zone / area / method references',
            value: `${row.id} / ${row.sourceZoneId} / ${row.inspectionAreaId} / ${row.methodAssignmentId}`,
          },
        ],
      }))
      return linked.length
        ? linked
        : [
            {
              title: section.title,
              rows: [{ label: 'Status', value: 'Not yet recorded' }],
            },
          ]
    }),
  ]
}
